import React, { useEffect, useState } from "react";
import { Users, UserPlus, Trash2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  { id: "station_owner", label: "Station Owner" },
  { id: "dj", label: "DJ" },
  { id: "social_media_manager", label: "Social Media Manager" },
];

const ROLE_COLOR = {
  super_admin: "bg-brand-onair/15 text-brand-onair border-brand-onair/40",
  station_owner: "bg-brand-amber/15 text-brand-amber border-brand-amber/40",
  dj: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  social_media_manager: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "dj" });

  const load = async () => {
    const res = await api.get("/team");
    setTeam(res.data);
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("Email and name required"); return; }
    try {
      await api.post("/team/invite", form);
      toast.success("Team member added");
      setShowModal(false);
      setForm({ email: "", name: "", role: "dj" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const updateRole = async (uid, role) => {
    try {
      await api.patch(`/team/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/team/${uid}`);
      toast.success("Removed from station");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="team-page">
      <PageHeader
        eyebrow="// TEAM"
        title="Your crew, your roles."
        description="Invite DJs, social media managers, and producers. Assign permissions."
        action={
          <button
            onClick={() => setShowModal(true)}
            data-testid="team-invite-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 font-semibold text-black amber-glow"
          >
            <UserPlus className="h-4 w-4" /> Invite member
          </button>
        }
      />

      {team.length === 0 ? (
        <div className="text-center py-20 text-ink-muted">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">No team members yet.</div>
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-bg-surface overflow-hidden" data-testid="team-list">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Member</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Email</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Role</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {team.map((m) => (
                <tr key={m.user_id} className="border-b border-line/50 hover:bg-bg-elevated transition" data-testid={`team-row-${m.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {m.picture ? (
                        <img src={m.picture} alt="" className="h-8 w-8 rounded-full" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-brand-amber/20 text-brand-amber font-display text-sm flex items-center justify-center">
                          {m.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm">{m.name} {m.user_id === user?.user_id && <span className="text-ink-muted">(you)</span>}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-secondary font-mono">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${ROLE_COLOR[m.role] || ""}`}>
                      {m.role.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {m.user_id !== user?.user_id && (
                      <div className="inline-flex items-center gap-2">
                        <Select value={m.role} onValueChange={(v) => updateRole(m.user_id, v)}>
                          <SelectTrigger className="w-44 bg-bg border-line text-xs" data-testid={`team-role-${m.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-bg-elevated border-line">
                            {ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button
                          onClick={() => remove(m.user_id)}
                          className="text-ink-muted hover:text-brand-onair transition"
                          data-testid={`team-remove-${m.user_id}`}
                          aria-label="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-bg-surface border-line text-ink-primary" data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Invite member</DialogTitle>
            <DialogDescription className="text-ink-secondary">
              They'll join your station with the role you assign.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-bg border-line" data-testid="invite-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-bg border-line" data-testid="invite-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 bg-bg border-line" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-elevated border-line">
                  {ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={invite} data-testid="invite-submit" className="rounded-md bg-brand-amber px-5 py-2 font-semibold text-black amber-glow">
              Add member
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
